import { useEffect, useRef } from "react";

/**
 * Corre `effect` UNA sola vez en la vida del componente, aunque StrictMode
 * monte el efecto dos veces.
 *
 * ── La trampa que este hook existe para prevenir ──────────────────────
 *
 * El patrón "arrancar algo asíncrono al montar" se escribió a mano dos
 * veces en este proyecto (el bootstrap de sesión y el callback de Google)
 * y las dos veces salió la MISMA combinación rota: un candado `useRef`
 * para correr una sola vez MÁS una bandera `alive` con cleanup para no
 * tocar estado tras desmontar. Por separado cada mitad es razonable;
 * juntas se anulan, y en StrictMode la pantalla se cuelga:
 *
 *   1. Montaje 1: el candado se cierra, arranca la promesa, se registra
 *      el cleanup.
 *   2. Desmontaje de prueba de StrictMode: el cleanup pone `alive = false`.
 *   3. Montaje 2: el efecto sale temprano por el candado — así que NO
 *      registra un cleanup nuevo y nadie vuelve a poner `alive = true`.
 *   4. La promesa resuelve: `alive` es `false`, la continuación se
 *      descarta y nunca se navega ni se cambia de estado. La pantalla se
 *      queda en "cargando" para siempre.
 *
 * Lo grave es que no truena: no hay error, no hay pantalla en blanco.
 * Solo un spinner eterno que en desarrollo se confunde con "el backend
 * tarda".
 *
 * La solución no es coordinar las dos mitades: es quitar una. El candado
 * por sí solo ya garantiza una única ejecución, y desde React 18
 * actualizar estado de un componente desmontado es inofensivo (no avisa
 * ni fuga). Por eso este hook NO acepta una función de limpieza — para
 * que la tercera pantalla que lo necesite no pueda equivocarse igual.
 *
 * Si algo de verdad necesita cancelarse al desmontar (una suscripción, un
 * `AbortController`, un `setInterval`), eso NO es este hook: es un
 * `useEffect` normal con su cleanup, que debe correr en cada montaje.
 *
 * `effect` se lee de una ref, así que puede ser una función nueva en cada
 * render sin que eso vuelva a dispararlo: no hay lista de dependencias
 * que mantener ni que se pueda equivocar.
 */
export function useRunOnce(effect: () => void): void {
  const ran = useRef(false);
  const latest = useRef(effect);
  latest.current = effect;

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    latest.current();
    // Sin cleanup, a propósito. Ver el comentario de arriba.
  }, []);
}
